import React, { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, List, PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import BlockRenderer from "../../components/course/BlockRenderer";
import { useCourseModules } from "../../contexts/CourseContext";
import { usePageTitle } from "../../hooks/usePageTitle";
import { HeadingEntry } from "../../utils/extractHeadings";
import { setLastModule } from "../../utils/courseLocalProgress";
import { LessonPageSkeleton } from "../../components/skeletons/PageSkeletons";
import "./course.css";

const CourseLessonPage: React.FC = () => {
  const { courseSlug = "", moduleSlug = "" } = useParams();
  const { courses, chapters, courseModules, loading } = useCourseModules();

  const course = courses.find((c) => c.slug === courseSlug);
  const module = courseModules.find((m) => m.courseSlug === courseSlug && m.slug === moduleSlug);
  const chapter = module ? chapters.find((c) => c.courseSlug === courseSlug && c.slug === module.chapterSlug) : undefined;

  const orderedModules = courseModules
    .filter((m) => m.courseSlug === courseSlug && m.published)
    .sort((a, b) => a.order - b.order);
  const moduleIndex = orderedModules.findIndex((m) => m.slug === moduleSlug);
  const adjacent = {
    previous: moduleIndex > 0 ? orderedModules[moduleIndex - 1] : undefined,
    next: moduleIndex >= 0 && moduleIndex < orderedModules.length - 1 ? orderedModules[moduleIndex + 1] : undefined,
  };

  usePageTitle(module ? `${module.number}: ${module.title}` : "Course module");

  const isReadable = !!module?.published;
  useEffect(() => {
    if (isReadable) setLastModule(courseSlug, moduleSlug);
  }, [isReadable, courseSlug, moduleSlug]);

  const [contentHeadings, setContentHeadings] = useState<HeadingEntry[]>([]);
  const [focusMode, setFocusMode] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem("course-reading-focus-mode") === "true";
    } catch {
      return false;
    }
  });

  const toggleFocusMode = () => {
    setFocusMode((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem("course-reading-focus-mode", String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [activeHash, setActiveHash] = useState<string>(() =>
    typeof window !== "undefined" ? window.location.hash.replace("#", "") : ""
  );

  useEffect(() => {
    const onHashChange = () => setActiveHash(window.location.hash.replace("#", ""));
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // Scrollspy: highlight whichever section is currently near the top of the
  // viewport as the reader scrolls, not just the one they last clicked.
  useEffect(() => {
    const ids = ["outcomes", ...contentHeadings.map((h) => h.id)];
    const elements = ids.map((id) => document.getElementById(id)).filter((el): el is HTMLElement => !!el);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible.length === 0) return;
        const topMost = visible.reduce((a, b) => (a.boundingClientRect.top <= b.boundingClientRect.top ? a : b));
        setActiveHash(topMost.target.id);
      },
      { rootMargin: "-110px 0px -70% 0px", threshold: 0 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [contentHeadings]);

  const scrollToHeading = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    window.history.pushState(null, "", `#${id}`);
    setActiveHash(id);
    setMobileTocOpen(false);

    // Wait for the state update above to commit and paint before grabbing
    // the element — otherwise a re-render triggered by setActiveHash can
    // swap the DOM node out from under an in-flight scrollIntoView call.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          el.focus({ preventScroll: true });
        }
      });
    });
  };

  if (loading) {
    return (
      <LessonPageSkeleton />
    );
  }

  if (!course || course.status !== "published" || !module || !module.published || !chapter || !chapter.published) {
    return <Navigate to={`/writing/series/${courseSlug || ""}`} replace />;
  }

  return (
    <article className="course-shell lesson-shell">
      <header className="lesson-header">
        <div className="course-wrap lesson-header__inner">
          <Link className="course-back-link" to={`/writing/series/${courseSlug}`}>
            <ArrowLeft aria-hidden="true" size={17} /> Full curriculum
          </Link>
          <div className="lesson-header__title">
            <span className="lesson-number">{chapter.number}</span>
            <div>
              <p>
                {course.title} · {chapter.title}
              </p>
              <h1>{module.title}</h1>
            </div>
          </div>
          <p className="lesson-deck">{chapter.centralQuestion}</p>
          <dl className="lesson-meta">
            <div><dt>Reading time</dt><dd>{chapter.readingTime} minutes</dd></div>
            <div><dt>Difficulty</dt><dd>{chapter.difficulty}</dd></div>
            {chapter.lastVerifiedAt && <div><dt>Verified</dt><dd>{chapter.lastVerifiedAt}</dd></div>}
          </dl>
        </div>
      </header>

      <button
        type="button"
        className="lesson-focus-toggle"
        onClick={toggleFocusMode}
        aria-pressed={focusMode}
        title={focusMode ? "Show table of contents" : "Hide table of contents (full-screen reading)"}
      >
        {focusMode ? (
          <PanelLeftOpen aria-hidden="true" size={18} />
        ) : (
          <PanelLeftClose aria-hidden="true" size={18} />
        )}
        <span className="sr-only">{focusMode ? "Show table of contents" : "Hide table of contents"}</span>
      </button>

      <button
        type="button"
        className="lesson-toc-drawer-toggle"
        onClick={() => setMobileTocOpen(true)}
        aria-label="Open table of contents"
      >
        <List aria-hidden="true" size={17} /> Contents
      </button>

      {mobileTocOpen && (
        <div className="lesson-toc-backdrop" onClick={() => setMobileTocOpen(false)} aria-hidden="true" />
      )}

      <div className={`course-wrap lesson-layout ${focusMode ? "lesson-layout--focus" : ""}`}>
        <aside
          className={`lesson-aside ${focusMode ? "is-focus-hidden" : ""} ${mobileTocOpen ? "is-open" : ""}`}
          aria-label="Lesson contents"
        >
          <div className="lesson-aside__mobile-header">
            <p>In this chapter</p>
            <button type="button" onClick={() => setMobileTocOpen(false)} aria-label="Close table of contents">
              <X aria-hidden="true" size={18} />
            </button>
          </div>
          <p className="lesson-aside__desktop-label">In this chapter</p>
          <nav>
            <a
              href="#outcomes"
              className={activeHash === "outcomes" ? "is-active" : ""}
              onClick={scrollToHeading("outcomes")}
            >
              Outcomes
            </a>
            {contentHeadings.map((heading) => (
              <a
                key={heading.id}
                href={`#${heading.id}`}
                className={`lesson-aside__link lesson-aside__link--level-${heading.level} ${
                  activeHash === heading.id ? "is-active" : ""
                }`}
                onClick={scrollToHeading(heading.id)}
              >
                {heading.text}
              </a>
            ))}
          </nav>
        </aside>

        <div className="lesson-content">
          <section id="outcomes" className="lesson-outcomes" aria-labelledby="outcomes-title" tabIndex={-1}>
            <h2 id="outcomes-title">By the checkpoint</h2>
            <ul>
              {chapter.outcomes.map((outcome) => (
                <li key={outcome}>{outcome}</li>
              ))}
            </ul>
          </section>

          <div id="content">
            <BlockRenderer
              blocks={module.blocks}
              scopeId={`${courseSlug}/${moduleSlug}`}
              onHeadingsChange={setContentHeadings}
            />
          </div>

          <nav className="lesson-pagination" aria-label="Course module navigation">
            {adjacent.previous ? (
              <Link to={`/writing/series/${courseSlug}/${adjacent.previous.slug}`}>
                <ArrowLeft aria-hidden="true" size={18} />
                <span><small>Previous</small>{adjacent.previous.number}: {adjacent.previous.title}</span>
              </Link>
            ) : <span />}
            {adjacent.next ? (
              <Link to={`/writing/series/${courseSlug}/${adjacent.next.slug}`}>
                <span><small>Next</small>{adjacent.next.number}: {adjacent.next.title}</span>
                <ArrowRight aria-hidden="true" size={18} />
              </Link>
            ) : (
              <Link to={`/writing/series/${courseSlug}`}>
                <span><small>Continue</small>Return to the curriculum</span>
                <ArrowRight aria-hidden="true" size={18} />
              </Link>
            )}
          </nav>
        </div>
      </div>
    </article>
  );
};

export default CourseLessonPage;
